import React from 'react';
import { theme } from '../styles/theme';
import { Logo } from './ui';

interface FooterProps {
  onNavigateToPrivacy?: () => void;
  onNavigateToTerms?: () => void;
}

/** Site footer with navigation, social destinations, and legal information. */
export const Footer: React.FC<FooterProps> = ({ onNavigateToPrivacy, onNavigateToTerms }) => {
  const currentYear = new Date().getFullYear();
  const navigationLinks = [
    { label: 'How it works', href: '#how-it-works' },
    { label: 'Clubs', href: '#features' },
    { label: 'Testimonials', href: '#testimonials' },
    { label: 'Pricing', href: '#pricing' },
  ];
  const legalLinks = [
    { label: 'Privacy Policy', href: '/privacy', onClick: onNavigateToPrivacy },
    { label: 'Terms of Service', href: '/terms', onClick: onNavigateToTerms },
  ];

  return (
    <footer className="match-footer">
      <div className="match-footer__watermark" aria-hidden="true">4KMATCH</div>
      <div className="match-footer__layout">
        <div className="match-footer__brand">
          <div className="match-footer__logo">
            <Logo size="lg" variant="full" colorScheme="light" />
          </div>
          <div className="match-footer__social-row">
            <span>Stay connected</span>
            <nav className="match-footer__socials" aria-label="4KMatch social media">
              <a href="https://www.instagram.com/4kmatch/" target="_blank" rel="noopener noreferrer"
                aria-label="4KMatch on Instagram (opens in a new tab)" title="Instagram">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true" focusable="false">
                  <rect x="3" y="3" width="18" height="18" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                </svg>
              </a>
              <a href="https://www.tiktok.com/@4kmatch.com" target="_blank" rel="noopener noreferrer"
                aria-label="4KMatch on TikTok (opens in a new tab)" title="TikTok">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="M14 3h3c.3 2.4 1.7 3.8 4 4v3a9 9 0 0 1-4-1.3V16a5 5 0 1 1-5-5v3a2 2 0 1 0 2 2V3Z" />
                </svg>
              </a>
              <a href="https://snapchat.com/t/qpOlPiMs" target="_blank" rel="noopener noreferrer"
                aria-label="4KMatch on Snapchat (opens in a new tab)" title="Snapchat">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="M12 3c-3 0-4.5 2-4.5 5v2l-1.6-.6c-.8-.3-1.3.8-.5 1.3l2.1 1c-.4 2-1.7 3.4-4 4.2.4 1 1.7 1.2 2.7 1.3l.5 1.5c2-.5 2.8 1.3 5.3 1.3s3.3-1.8 5.3-1.3l.5-1.5c1-.1 2.3-.3 2.7-1.3-2.3-.8-3.6-2.2-4-4.2l2.1-1c.8-.5.3-1.6-.5-1.3l-1.6.6V8c0-3-1.5-5-4.5-5Z" />
                </svg>
              </a>
            </nav>
          </div>
        </div>

        <div className="match-footer__main">
          <div className="match-footer__columns">
            <nav aria-label="Footer navigation">
              <h2 className="match-footer__label">Navigation</h2>
              <ul className="match-footer__links">
                {navigationLinks.map((link) => (
                  <li key={link.href}><a href={link.href}>{link.label}</a></li>
                ))}
              </ul>
            </nav>
            <nav aria-label="Footer legal links">
              <h2 className="match-footer__label">Company &amp; legal</h2>
              <ul className="match-footer__links">
                {legalLinks.map((link) => (
                  <li key={link.href}>
                    <a href={link.href}
                      target={link.onClick ? undefined : '_blank'}
                      rel={link.onClick ? undefined : 'noopener noreferrer'}
                      onClick={link.onClick ? (event) => {
                        if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
                          event.preventDefault();
                          link.onClick!();
                        }
                      } : undefined}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="match-footer__app">
              <div className="match-footer__app-mark" aria-hidden="true">
                <img src="/images/logo_light.png" alt="" width="48" height="48" />
              </div>
              <p className="match-footer__app-title">Your next matchday starts here.</p>
              <a className="match-footer__cta" href="https://apps.apple.com/app/id6758546772" target="_blank" rel="noopener noreferrer">
                Get the app
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </a>
              <p className="match-footer__platforms">For iOS and Android.</p>
            </div>
          </div>
          <div className="match-footer__details">
            <div className="match-footer__company">
              <span className="match-footer__company-name">Kumba Media</span>
              <span>KvK registration: 99235137</span>
              <span>Rotterdam 3071 AP</span>
            </div>
            <p className="match-footer__copyright">&copy; {currentYear} Kumba Media. All rights reserved.</p>
          </div>
        </div>
      </div>

      <style>{`
        .match-footer {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          padding: 20px 24px 24px;
          background: ${theme.colors.bgPrimary};
          color: ${theme.colors.textPrimary};
          font-family: ${theme.fonts.primary};
          border-top: 1px solid ${theme.colors.border};
        }
        .match-footer *, .match-footer *::before, .match-footer *::after { box-sizing: border-box; }
        .match-footer::before {
          content: '';
          position: absolute;
          z-index: -1;
          inset: 0;
          pointer-events: none;
          background: radial-gradient(ellipse at 15% 75%, rgba(255, 39, 0, .09), transparent 55%),
                      radial-gradient(ellipse at 85% 100%, rgba(255, 0, 168, .05), transparent 55%);
        }
        .match-footer__watermark {
          position: absolute;
          z-index: -1;
          bottom: -.21em;
          left: 50%;
          transform: translateX(-50%);
          font-size: clamp(100px, 19vw, 270px);
          font-weight: 900;
          letter-spacing: -.065em;
          line-height: 1;
          color: rgba(255, 255, 255, .025);
          white-space: nowrap;
          pointer-events: none;
          user-select: none;
        }
        .match-footer__layout {
          display: grid;
          grid-template-columns: minmax(0, 320px) minmax(0, 1fr);
          gap: 16px;
          max-width: 1200px;
          margin: 0 auto;
        }
        .match-footer__brand, .match-footer__main {
          min-width: 0;
          padding: 24px;
          border: 1px solid rgba(255, 255, 255, .1);
          border-radius: 24px;
        }
        .match-footer__brand {
          display: flex;
          flex-direction: column;
          gap: 20px;
          background: radial-gradient(ellipse at 100% 100%, rgba(255, 0, 168, .2), transparent 70%),
                      linear-gradient(145deg, #47100e 0%, #210b10 55%, #130c12 100%);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, .05), 0 12px 40px rgba(255, 39, 0, .04);
        }
        /* Frame the existing mark without allocating space to its square asset's empty edges. */
        .match-footer__logo { width: 144px; max-width: 100%; aspect-ratio: 1.2; overflow: hidden; }
        .match-footer__logo > div { width: 166.6667%; transform: translate(-20%, -25%); }
        .match-footer__logo img { width: 100% !important; max-width: none; height: auto !important; }
        .match-footer__label {
          margin: 0 0 12px;
          color: #c3bec2;
          font-size: 20px;
          font-weight: 600;
          letter-spacing: 0;
          line-height: 1.5;
        }
        .match-footer__social-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-top: 0;
          color: #c6b6bd;
          font-size: 16px;
        }
        .match-footer__socials { display: flex; gap: 8px; }
        .match-footer__socials a {
          display: grid;
          place-items: center;
          width: 44px;
          height: 44px;
          border: 1px solid rgba(255, 255, 255, .12);
          border-radius: 12px;
          color: #fff;
          background: rgba(0, 0, 0, .28);
          transition: background ${theme.transitions.fast}, border-color ${theme.transitions.fast};
        }
        .match-footer__socials svg { width: 19px; height: 19px; }
        .match-footer__socials a:hover { background: rgba(255, 0, 168, .15); border-color: rgba(255, 255, 255, .35); }
        .match-footer__main {
          display: flex;
          flex-direction: column;
          gap: 16px;
          background: linear-gradient(145deg, #121113, #0b0a0c);
        }
        .match-footer__columns {
          display: grid;
          grid-template-columns: minmax(0, 140px) minmax(0, 155px) minmax(0, 1fr);
          align-items: start;
          gap: 16px;
          width: 100%;
          max-width: 700px;
        }
        .match-footer__links { display: grid; gap: 8px; list-style: none; padding: 0; margin: 0; }
        .match-footer__links a {
          display: inline-flex;
          align-items: center;
          min-height: 24px;
          padding: 0;
          color: #ddd9dd;
          font-size: 16px;
          font-weight: 500;
          line-height: 1.5;
          text-decoration: none;
          transition: color ${theme.transitions.fast};
        }
        .match-footer__links a:hover { color: #ff709f; }
        .match-footer a:focus-visible { outline: 2px solid #ff709f; outline-offset: 5px; border-radius: 4px; }
        .match-footer__app {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 64px;
          align-items: start;
          column-gap: 16px;
          min-width: 0;
        }
        .match-footer__app-mark {
          display: grid;
          place-items: center;
          width: 64px;
          height: 64px;
          grid-column: 2;
          grid-row: 1 / span 3;
          margin: 4px 0 0;
          border: 1px solid rgba(255, 255, 255, .2);
          border-radius: 19px;
          background: ${theme.colors.gradientPrimary};
          box-shadow: inset 0 1px 1px rgba(255, 255, 255, .25), 0 8px 30px rgba(255, 0, 168, .15);
          transform: rotate(9deg);
        }
        .match-footer__app-mark img { width: 38px; height: 38px; object-fit: contain; }
        .match-footer__app-title {
          grid-column: 1;
          grid-row: 1;
          max-width: 210px;
          margin: 0 0 12px;
          font-size: 16px;
          font-weight: 600;
          letter-spacing: -.035em;
          line-height: 1.3;
        }
        .match-footer__cta {
          grid-column: 1;
          grid-row: 2;
          justify-self: start;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          min-height: 44px;
          max-width: 100%;
          padding: 10px 14px;
          border: 1px solid rgba(255, 255, 255, .14);
          border-radius: 12px;
          background: linear-gradient(rgba(0, 0, 0, .24), rgba(0, 0, 0, .24)), ${theme.colors.gradientPrimary};
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          line-height: 1.4;
          text-decoration: none;
          transition: box-shadow ${theme.transitions.fast};
        }
        .match-footer__cta:hover { box-shadow: 0 4px 24px rgba(255, 0, 168, .25); }
        .match-footer__cta svg { flex-shrink: 0; }
        .match-footer__platforms { grid-column: 1; grid-row: 3; margin: 10px 0 0; color: #a49fa6; font-size: 16px; line-height: 1.5; }
        .match-footer__details {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 8px 16px;
          margin-top: 0;
          padding-top: 12px;
          border-top: 1px solid rgba(255, 255, 255, .08);
          color: #a49fa6;
          font-size: 16px;
          line-height: 1.5;
        }
        .match-footer__company { display: flex; flex-wrap: wrap; align-items: center; gap: 2px 12px; }
        .match-footer__company-name { flex-basis: 100%; }
        .match-footer__company-name { color: #ddd9dd; font-size: 16px; font-weight: 500; }
        .match-footer__copyright { margin: 0; }

        @media (max-width: 1100px) {
          .match-footer__brand, .match-footer__main { padding: 20px 24px; }
          .match-footer__layout { grid-template-columns: minmax(0, 1fr) minmax(0, 1.8fr); }
          .match-footer__columns { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
          .match-footer__app {
            grid-column: 1 / -1;
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto;
            align-items: center;
            gap: 8px 20px;
            padding-top: 12px;
            border-top: 1px solid rgba(255, 255, 255, .08);
          }
          .match-footer__app-mark { display: none; }
          .match-footer__app-title { margin: 0; font-size: 16px; }
          .match-footer__cta { grid-column: 2; grid-row: 1 / span 2; gap: 12px; }
          .match-footer__platforms { grid-row: 2; margin: 0; }
        }
        @media (max-width: 1000px) {
          .match-footer__layout { grid-template-columns: minmax(0, 1fr); gap: 12px; }
        }
        @media (max-width: 700px) {
          .match-footer { padding: 20px 16px 24px; }
          .match-footer__layout { grid-template-columns: minmax(0, 1fr); gap: 12px; }
          .match-footer__brand, .match-footer__main { padding: 20px; border-radius: 20px; }
          .match-footer__brand { gap: 20px; }
          .match-footer__main { gap: 16px; }
          .match-footer__label { margin-bottom: 12px; font-size: 20px; }
          .match-footer__details { gap: 12px; padding-top: 12px; }
        }
        @media (max-width: 360px) {
          .match-footer__columns { grid-template-columns: minmax(0, 1fr); }
          .match-footer__app { grid-template-columns: minmax(0, 1fr); }
          .match-footer__cta { grid-column: 1; grid-row: 2; }
          .match-footer__platforms { grid-row: 3; }
          .match-footer__brand, .match-footer__main { padding: 16px 20px; }
          .match-footer__app { column-gap: 12px; }
          .match-footer__cta { gap: 8px; padding: 10px 12px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .match-footer a { transition: none; }
        }
      `}</style>
    </footer>
  );
};

export default Footer;
